import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    CardContent,
    Stack,
    IconButton,
    Tooltip,
    Grid,
    CardMedia,
    Divider,
} from '@mui/material';
import {
    PlayArrow,
    AccessTime,
    Visibility,
    Message,
    ThumbUp,
    CalendarToday,
    ContentCopy,
} from '@mui/icons-material';
import { SponsorMention } from '../types';
import { formatDuration } from '../utils/time';
import { formatChileanDate } from '../utils/dateUtils';

interface SponsorDetailMentionsProps {
    mentions: SponsorMention[];
}

export const SponsorDetailMentions: React.FC<SponsorDetailMentionsProps> = ({ mentions }) => {
    const { t } = useTranslation('explorar');
    if (!mentions || mentions.length === 0) {
        return null;
    }

    const podcast = mentions[0].podcast;

    const handleCopyId = (id: string) => {
        navigator.clipboard.writeText(id);
    };

    return (
        <Box>
            <Typography variant="h3" sx={{ mb: 2 }}>
                {podcast.title}
            </Typography>

            <Stack spacing={2}>
                {mentions.map((mention, index) => {
                    const episode = mention.episode;
                    const thumbnailUrl = episode.thumbnail || '/default-thumbnail.jpg';

                    return (
                        <React.Fragment key={mention._id}>
                            <Grid container sx={{ p: 0, minHeight: '120px' }}>
                                <Grid
                                    size={{ xs: 12, sm: 4, md: 3, lg: 2 }}
                                    sx={{
                                        position: 'relative',
                                        minHeight: { md: '120px' },
                                    }}
                                >
                                    <Box>
                                        <CardMedia
                                            component="img"
                                            image={thumbnailUrl}
                                            alt={episode.title}
                                            sx={{
                                                height: '100%',
                                                minWidth: '120px',
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                p: 1,
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 8, md: 9, lg: 10 }}>
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography component="div" variant="h6" sx={{ fontWeight: 600 }}>
                                            {episode.title}
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap' }}
                                        >
                                            {episode.views && (
                                                <Tooltip title={t('mentions.views')}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                    >
                                                        <Visibility
                                                            sx={{
                                                                fontSize: 16,
                                                                verticalAlign: 'middle',
                                                            }}
                                                        />
                                                        {Number(episode.views)?.toLocaleString()}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                            {episode.likes && (
                                                <Tooltip title={t('mentions.likes')}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <ThumbUp sx={{ fontSize: 16 }} />{' '}
                                                        {Number(episode.likes)?.toLocaleString()}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                            {episode.comments && (
                                                <Tooltip title={t('mentions.comments')}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Message sx={{ fontSize: 16 }} />{' '}
                                                        {Number(episode.comments)?.toLocaleString()}
                                                    </Typography>
                                                </Tooltip>
                                            )}
                                            {process.env.NODE_ENV === 'development' && (
                                                <Tooltip title="Copy ID">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                cursor: 'pointer',
                                                                '&:hover': { color: 'primary.main' },
                                                            }}
                                                            onClick={() => handleCopyId(episode._id)}
                                                        >
                                                            {episode._id}
                                                        </Typography>
                                                        <IconButton size="small" onClick={() => handleCopyId(episode._id)}>
                                                            <ContentCopy sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={t('mentions.published')}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <CalendarToday sx={{ fontSize: 14 }} />
                                                    {formatChileanDate(episode.publishedAt)}
                                                </Typography>
                                            </Tooltip>
                                        </Stack>

                                        {/* Mention Text */}
                                        {mention.text && (
                                            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="body2" fontStyle="italic">
                                                    "{mention.text}"
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Mention Details */}
                                        <Stack direction="row" spacing={2} sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Tooltip title={t('mentions.timeRange')}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                >
                                                    <AccessTime sx={{ fontSize: 16 }} />
                                                    {formatDuration(mention.startTime)} - {formatDuration(mention.endTime)}
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip title={t('mentions.duration')}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                >
                                                    <AccessTime sx={{ fontSize: 16 }} />
                                                    {formatDuration(mention.duration)}
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip title={t('mentions.goToTimestamp')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (episode.url) {
                                                            const url = new URL(episode.url);
                                                            url.searchParams.set('t', Math.floor(mention.startTime).toString());
                                                            window.open(url.toString(), '_blank');
                                                        }
                                                    }}
                                                >
                                                    <PlayArrow />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </CardContent>
                                </Grid>
                            </Grid>
                            {index < mentions.length - 1 && (
                                <Divider sx={{ my: 2 }} />
                            )}
                        </React.Fragment>
                    );
                })}
            </Stack>
        </Box>
    );
}; 